// src/pages/Tools/Tools.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Database, Mail, Globe, ShieldQuestion, Network, Bug, Check,
  FileSearch, Eye, ScanLine, Layers, Layers3, ShieldCheck as ShieldCheckIcon,
  Image as ImageIcon, AlertTriangle
} from 'lucide-react';

import {
  getPremiumMe, type ToolKey, type PremiumMeResponse, type PremiumQuotas
} from '../../../api/premiumService';
import { startExecuteTool, getExecuteJob } from '../../../api/premiumService';

import ToolsList from '../../../components/Tools/ToolsList';
import ToolPanel from '../../../components/Tools/ToolPanel';

type Props = { isPremium: boolean; goToPremium?: () => void };

const ICONS: Record<ToolKey, React.ComponentType<any>> = {
  leaked_data: Database, emails: Mail, ips: Globe, ipwhois: ShieldQuestion,
  subdomains: Network, typosquatting: Bug, names_tld: FileSearch, whois: Eye,
  dns_all_records: ScanLine, wafw00f: ShieldQuestion, wappalyzer: Layers, gau: Layers3,
  spoofornot: ShieldCheckIcon, screenshot: ImageIcon,
};

const ORDER: ToolKey[] = [
  'leaked_data','emails','ips','ipwhois','subdomains',
  'typosquatting','names_tld','whois','dns_all_records','wafw00f',
  'wappalyzer','gau','spoofornot','screenshot'
];

const ALL_TOOLS = ORDER.map((id) => ({
  id,
  icon: ICONS[id],
  titleKey: `tools.${id}`,
  descKey: `tools_desc.${id}`,
  placeholderKey: `tools.placeholders.${id}`,
}));

// ---------- utils ----------
function errorToString(err: any): string {
  if (!err) return 'Unknown error';
  const top = err?.response?.data ?? err?.data ?? err?.detail ?? err;
  if (typeof top === 'string') return top;
  if (Array.isArray(top?.detail)) return top.detail.map((x: any) => x?.msg ?? JSON.stringify(x)).join('; ') || 'Request validation error';
  if (Array.isArray(top)) return top.map((x: any) => x?.msg ?? JSON.stringify(x)).join('; ') || 'Error';
  const s = top?.detail ?? top?.msg;
  if (typeof s === 'string') return s;
  try { return JSON.stringify(top); } catch { return String(top); }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOMAIN_RE = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

// ===== Mini dropdown bonito (inline, sin dependencias) =====
function MiniSelect({
  value,
  onChange,
  disabled,
  premiumStyle = true,
  labels,
}: {
  value: 'domain'|'email';
  onChange: (v: 'domain'|'email') => void;
  disabled?: boolean;
  premiumStyle?: boolean;
  labels: { domain: string; email: string; aria: string };
}) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef<HTMLButtonElement|null>(null);
  const menuRef = React.useRef<HTMLUListElement|null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current || !menuRef.current) return;
      if (!btnRef.current.contains(t) && !menuRef.current.contains(t)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const baseBtn =
    'inline-flex items-center gap-2 px-3 h-[42px] rounded-lg text-white text-sm font-semibold transition';
  const grad = premiumStyle
    ? 'bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-700 hover:to-indigo-700'
    : 'bg-slate-700 hover:bg-slate-800';

  const Item = ({
    icon,
    label,
    active,
    selected,
    onClick,
  }: {
    icon: React.ReactNode;
    label: string;
    active?: boolean;
    selected?: boolean;
    onClick: () => void;
  }) => (
    <li
      role="option"
      aria-selected={selected}
      className={[
        'flex items-center justify-between gap-3 px-3 py-2.5 text-sm cursor-pointer select-none rounded-md',
        active ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-slate-50 text-slate-700'
      ].join(' ')}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        <span className="opacity-80">{icon}</span>
        <span className={selected ? 'font-semibold text-slate-900' : ''}>{label}</span>
      </div>
      {selected ? <Check className="h-4 w-4 text-indigo-600" /> : null}
    </li>
  );

  return (
    <div className="relative inline-flex">
      {/* Botón */}
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={`${baseBtn} ${grad} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={labels.aria}
      >
        {value === 'domain' ? labels.domain : labels.email}
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 20 20" className="ml-1">
          <path className="fill-white" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.17l3.71-2.94a.75.75 0 1 1 .94 1.17l-4.24 3.36a.75.75 0 0 1-.94 0L5.21 8.4a.75.75 0 0 1 .02-1.19z"/>
        </svg>
      </button>

      {/* Menú */}
      {open && (
        <div className="absolute right-0 top-[46px] z-50" style={{ transformOrigin: 'top right' }}>
          <div className="rounded-xl border border-slate-200 shadow-2xl bg-white/95 backdrop-blur-sm overflow-hidden min-w-[210px]">
            {/* Stripe superior con degradado */}
            <div className="h-1 w-full bg-gradient-to-r from-sky-500 via-violet-500 to-indigo-600" />
            <ul ref={menuRef} role="listbox" tabIndex={-1} className="p-2">
              <Item
                icon={<Globe className="h-4 w-4" />}
                label={labels.domain}
                selected={value === 'domain'}
                active={value === 'domain'}
                onClick={() => { onChange('domain'); setOpen(false); }}
              />
              <Item
                icon={<Mail className="h-4 w-4" />}
                label={labels.email}
                selected={value === 'email'}
                active={value === 'email'}
                onClick={() => { onChange('email'); setOpen(false); }}
              />
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
// ===== fin MiniSelect =====

const Tools: React.FC<Props> = ({ isPremium, goToPremium }) => {
  const { t } = useTranslation();

  const [quotas, setQuotas] = React.useState<PremiumQuotas | null>(null);
  const [activeTool, setActiveTool] = React.useState<ToolKey>('leaked_data');
  const [input, setInput] = React.useState('');
  const [results, setResults] = React.useState<any[]>([]);
  const [running, setRunning] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [jobId, setJobId] = React.useState<string | null>(null);

  const [queryType, setQueryType] = React.useState<'domain' | 'email'>('domain');

  const runRef = React.useRef(0);
  const def = React.useMemo(() => ALL_TOOLS.find(d => d.id === activeTool)!, [activeTool]);

  React.useEffect(() => {
    let mounted = true;
    const zero: PremiumQuotas = {
      leaked_data:0, emails:0, employees:0, ips:0, ipwhois:0, subdomains:0,
      typosquatting:0, names_tld:0, whois:0, dns_all_records:0, wafw00f:0,
      wappalyzer:0, gau:0, spoofornot:0, screenshot:0
    };

    (async () => {
      if (!isPremium) { mounted && setQuotas(zero); return; }
      try {
        const me: PremiumMeResponse = await getPremiumMe();
        mounted && setQuotas(me.quotas);
      } catch {
        mounted && setQuotas(zero);
      }
    })();

    return () => { mounted = false; };
  }, [isPremium]);

  const buildPayload = React.useCallback(() => {
    const val = input.trim();
    switch (activeTool) {
      case 'subdomains':
      case 'wafw00f':
        return { tool: activeTool, url: val || 'https://example.com' };
      case 'dns_all_records':
      case 'whois':
      case 'gau':
      case 'typosquatting':
      case 'spoofornot':
      case 'ipwhois':
        return { tool: activeTool, domain: val || 'example.com' };
      case 'leaked_data':
        return queryType === 'email'
          ? { tool: activeTool, email: val || 'user@example.com', filterType: 'email' }
          : { tool: activeTool, domain: val || 'example.com', filterType: 'domain' };
      case 'wappalyzer':
      case 'screenshot': {
        const u = val || 'https://example.com';
        const url = /^https?:\/\//i.test(u) ? u : `https://${u}`;
        return { tool: activeTool, url };
      }
      case 'emails':
        return { tool: activeTool, email: val || 'user@example.com' };
      case 'ips':
        return { tool: activeTool, domain: val || 'example.com' };
      case 'names_tld':
        return { tool: activeTool, name: val || 'acme' };
      default:
        return { tool: activeTool };
    }
  }, [activeTool, input, queryType]);

  const placeholder = React.useMemo(() => {
    if (activeTool === 'leaked_data') {
      return queryType === 'email'
        ? t('tools.examples.email')   // ej. user@example.com
        : t('tools.examples.domain'); // ej. acme.com
    }
    return t(def.placeholderKey);
  }, [activeTool, queryType, def.placeholderKey, t]);

  const handleRun = async () => {
    if (activeTool === 'leaked_data') {
      const val = input.trim();
      if (queryType === 'email' && val && !EMAIL_RE.test(val)) { setErrorMsg(t('tools.errors.email_format')); return; }
      if (queryType === 'domain' && val && !DOMAIN_RE.test(val)) { setErrorMsg(t('tools.errors.domain_format')); return; }
    }

    const myRun = ++runRef.current;
    setErrorMsg(null);
    setResults([]);
    setRunning(true);

    try {
      const start = await startExecuteTool(buildPayload());
      setJobId(start.job_id);
      setQuotas(prev => prev ? { ...prev, [activeTool]: start.remaining_quota } : prev);

      const POLL_MS = 1500;
      const MAX_MS = 10 * 60 * 1000;
      const t0 = Date.now();

      for (;;) {
        if (myRun !== runRef.current) break;
        const state = await getExecuteJob(start.job_id);
        if (myRun !== runRef.current) break;

        if (state.status === 'done') {
          const payload = state.payload;
          const normalized = Array.isArray(payload) ? payload : [payload];
          setResults(normalized);
          break;
        }
        if (state.status === 'error') {
          setErrorMsg(errorToString(state.payload));
          break;
        }
        if (Date.now() - t0 > MAX_MS) {
          setErrorMsg(t('tools.timeout'));
          break;
        }
        await new Promise(r => setTimeout(r, POLL_MS));
      }
    } catch (err: any) {
      setErrorMsg(errorToString(err));
    } finally {
      if (myRun === runRef.current) {
        setRunning(false);
        setJobId(null);
      }
    }
  };

  const handleCancelLocal = () => {
    runRef.current++;
    setRunning(false);
    setJobId(null);
  };

  return (
    <div className="w-full">
      <div className="absolute inset-0 -z-10 bg-slate-50 
      [background:radial-gradient(1200px_600px_at_50%_-20%,rgba(99,102,241,0.08),transparent_50%),radial-gradient(800px_400px_at_10%_20%,rgba(14,165,233,0.08),transparent_50%)]" />

      <div className="mb-6">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-extrabold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent drop-shadow-sm">
            {t('tools.title')}
          </h1>
          <p className="mt-2 text-lg text-gray-600 italic tracking-wide">
            {t('tools.subtitle')}
          </p>
        </div>

        {!isPremium && (
          <div className="mt-3 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900">
            <AlertTriangle className="h-5 w-5 mt-0.5" />
            <p className="text-sm">
              {t('tools.banner_non_premium')}{' '}
              <button type="button" onClick={goToPremium} className="underline font-medium">
                {t('tools.go_premium')}
              </button>
            </p>
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
        <ToolsList
          tools={ALL_TOOLS}
          active={activeTool}
          quotas={quotas}
          onSelect={(id) => {
            runRef.current++;
            setActiveTool(id);
            setResults([]);
            setInput('');
            setErrorMsg(null);
            setRunning(false);
            setJobId(null);
            if (id !== 'leaked_data') setQueryType('domain');
          }}
          maxHeight={200}
        />

        <ToolPanel
          title={t(def.titleKey)}
          desc={t(def.descKey)}
          Icon={def.icon}
          isPremium={isPremium}
          running={running}
          input={input}
          setInput={setInput}
          onRun={handleRun}
          onCancel={handleCancelLocal}
          canCancel={!!jobId}
          results={results}
          activeTool={activeTool}
          errorMsg={errorMsg}
          placeholder={placeholder}
          rightActionAddon={activeTool === 'leaked_data' ? (
            <MiniSelect
              value={queryType}
              onChange={setQueryType}
              disabled={!isPremium}
              premiumStyle={isPremium}
              labels={{
                domain: t('tools.select.domain'),
                email:  t('tools.select.email'),
                aria:   t('tools.select.aria'),
              }}
            />
          ) : undefined}
        />
      </div>
    </div>
  );
};

export default Tools;
